import * as yup from 'yup';
import { FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, yupResolver } from '@mantine/form';
import { Space, Flex, Text } from '@mantine/core';
import { Auth } from 'aws-amplify';
import { TextInput, Link } from '@common/components';
import { validationPattern } from '@common/helpers';
import { useFormFocus } from '@common/hooks';
import { AuthFormHoc } from 'src/hoc';
import { IRecoverEmail } from '@common/types/types';
import { PATHS } from 'src/constants';
import  styles from "./styles.module.scss"
const initialValues: IRecoverEmail = {
  email: '',
};

const validationFormSchema = yup.object().shape({
  email: validationPattern.email,
});

const RecoverPasswordForm: FC = (): JSX.Element => {
  const navigate = useNavigate();
  const [t] = useTranslation('auth');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const form = useForm({
    initialValues: initialValues,
    validate: yupResolver(validationFormSchema),
  });
  const { setNeedFocus } = useFormFocus({ formErrors: form.errors });

  const [systemError, setSystemError] = useState<null | string>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (systemError) {
      errorRef.current?.setAttribute('tabindex', '0');
      errorRef.current?.focus();
    }
  }, [systemError]);

  const onErrorHandler = (): void => {
    setNeedFocus(true);
    setSystemError(null);
  };

  const onSubmitHandler = async (values: IRecoverEmail): Promise<void> => {
    setIsLoading(true);
    setNeedFocus(true);
    setSystemError(null);
    try {
      await Auth.forgotPassword(values.email);
      navigate(PATHS.SEND_VERIFY_LINK, {
        state: {
          email: values.email,
          isRecover: true,
        },
      });
    } catch (error: any) {
      if (error.code === 'UserNotFoundException') {
        setSystemError(`${t('userNotFoundException')}`);
      } else if (error instanceof Error) {
        setSystemError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex style={{ flex : '1'}} direction={{ base: 'column' }}  justify={"center"} >
      <AuthFormHoc
        classContainer={styles.container}
        title={t('recoverPassword')}
        description={<p>{t('confirmRecoverEmail')}</p>}
        form={form}
        submitText={`${t('confirm')}`}
        onSubmitHandler={onSubmitHandler}
        onErrorHandler={onErrorHandler}
        isLoading={isLoading}>
        <>
          {systemError && (
            <Flex ref={errorRef}>
              <Text color={'#a80000'}>{systemError}</Text>
            </Flex>
          )}
          <TextInput
            withAsterisk
            label={`${t('email')}`}
            placeholder={`${t('enterYour')} ${t('email')}`}
            ariaLabel={`${t('emailDescribe')}`}
            {...form.getInputProps('email')}
          />
        </>
      </AuthFormHoc>
      {/* <Space h={'md'} /> */}
      <p className={styles.passwordDescription}>
          { t('rememberPassword')}
         <Link navigateTo={PATHS.LOGIN}>{t('backToLogin')}</Link>
      </p>
      <p className={ styles.contactDescription }>
        {t('contacusDescription')}
        <Link navigateTo="mailto:hello@vially.io">hello@vially.io</Link>
      </p>
    </Flex>
  );
};

export default RecoverPasswordForm;
